// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — 3, 2, 1, and then the night starts.
//
// A round never begins the instant the board appears. Without this, whoever happened to be looking
// at their screen when the host's start arrived gets a free head start, and the table reads as a
// jump cut. Each peer counts locally from the start message, which puts everyone within one network
// hop of each other — and the round has no clock of its own, so that is exact enough.
//
// setInterval, never rAF: a backgrounded tab never fires rAF, so a peer who tabbed away during the
// count would sit on "3" for ever and then find the round half over.

export interface CountdownDeps {
  container: HTMLElement;
  sfx: (name: string) => void;
  onDone: () => void;
  /** Skipped in tests and in solo, where there is nobody to be fair to. */
  from?: number;
  stepMs?: number;
}

export function runCountdown(deps: CountdownDeps): { cancel: () => void } {
  const from = deps.from ?? 3;
  const step = deps.stepMs ?? 700;
  const wrap = document.createElement('div');
  wrap.className = 'countdown';
  wrap.setAttribute('aria-live', 'assertive');
  const num = document.createElement('div');
  num.className = 'countdown-n';
  wrap.append(num);
  deps.container.append(wrap);

  let n = from;
  let done = false;
  const paint = (): void => {
    num.textContent = n > 0 ? String(n) : 'GO';
    num.classList.remove('pop');
    void num.offsetWidth; // restart the animation
    num.classList.add('pop');
    // The audio is what actually carries this: players watch the table, not the overlay.
    deps.sfx(n > 0 ? 'tick' : 'go');
  };
  paint();

  const timer = setInterval(() => {
    n--;
    if (n < 0) {
      finish();
      return;
    }
    paint();
  }, step);

  function finish(): void {
    if (done) return;
    done = true;
    clearInterval(timer);
    wrap.remove();
    deps.onDone();
  }

  return {
    cancel: () => {
      if (done) return;
      done = true;
      clearInterval(timer);
      wrap.remove();
    },
  };
}
